/**
 * Canonical RBAC resources and actions used by backoffice and partner APIs.
 *
 * A permission is encoded as `resource:action`, e.g. `member:read`.
 * Wildcards: `resource:*` grants all actions on a resource, `*:*` grants everything (reserved for Root/Super Admin).
 */

export const RESOURCES = [
  // platform
  'system',
  'audit',
  'monitor',
  'reconcile',
  'setting',
  'report',
  'notify',
  // maker-checker (two-person approval wrapper)
  'approval',
  // the largest single amount a role or a named person may decide alone
  'approval_limit',
  // the CS → Finance support ticket
  'ticket',
  // which back-office staff are on shift
  'staff_presence',
  // tenancy hierarchy + frontends catalog
  'tenancy',
  'frontend',
  // per-tenant white-label site config (distinct from platform-wide frontend)
  'site',
  // identity
  'admin',
  'role',
  'member',
  'member_group',
  'member_blacklist',
  'partner',
  // money
  'wallet',
  'transaction',
  'deposit',
  'withdraw',
  'bank',
  'bank_account',
  // gaming
  'game',
  'provider',
  'bet',
  'bet_limit',
  'winloss',
  // bonus
  'promotion',
  'promotion_claim',
  'coupon',
  'cashback',
  'cashback_claim',
  'commission',
  'affiliate',
  'settlement',
  'mlm',
  // engagement
  'login_month',
  'mission',
  'lucky_spin',
  'leaderboard',
  // communications
  'announcement',
  'popup',
  'template',
  // game art (svc-art): read the art records, approve Recast tiles, update a
  // game's art (redo, path, title, batch), manage the Tile Kit, models and
  // casting rules. One svc-art serves every environment (ADR-0022), so a grant
  // here acts on the tiles every agent shows.
  'art',
] as const;

export type Resource = (typeof RESOURCES)[number];

export const ACTIONS = [
  'read',
  'create',
  'update',
  'delete',
  'approve',
  'reject',
  'export',
  'settle',
  'run',
  'cancel',
  'payout_cancel',
  'adjust',
  'publish',
  'impersonate',
  // back-office staff clearing a player's password — split out of
  // `member:update` so a CS lead can hold it without the rest of the CRUD
  'reset_password',
  // showing ONE masked personal field in full, recorded per reveal (ADR-0003)
  'reveal_pii',
  // mlm tree-browser read + per-provider PT money-config write (P5)
  'node_read',
  'pt_set',
  // mlm prepaid-credit balance read + top-up/adjust money-move write (plan A T4)
  'credit_read',
  'credit_set',
  // white-label per-tenant site-config self-service (site:manage)
  'manage',
] as const;

export type Action = (typeof ACTIONS)[number];

export type Permission = `${Resource}:${Action}` | `${Resource}:*` | '*:*';

/**
 * Actions that are deliberately NOT produced by a `<resource>:*` wildcard. They
 * are account-takeover primitives whose blast radius must stay narrow: only the
 * super wildcard `*:*` or an EXPLICIT `<resource>:impersonate` grant resolves
 * them. This stops `member:*` (held by operator/manager presets) from silently
 * granting login-as-player, and `admin:*` from granting login-as-child-node.
 * See docs/superpowers/specs/2026-06-22-login-as-member-design.md §5 (fix H1).
 *
 * EXPORTED because `expand` and `allows` are not the only two places that
 * answer "does this role hold that code". Every OTHER candidate builder has to
 * honour the same carve-out or it becomes the way around it: svc-admin's
 * `/rbac/check` (the gateway resolver's only source of truth for a non-root
 * caller) and the backoffice auth store both build a `<resource>:*` candidate
 * of their own. Import this rather than re-typing the list.
 */
export const WILDCARD_EXCLUDED_ACTIONS: ReadonlySet<Action> = new Set<Action>([
  'impersonate',
  // `reveal_pii` and `reset_password` are the same shape of primitive as
  // `impersonate`: one hands a staff member a player's real phone number and
  // bank account, the other hands them the account itself. `member:*` is held
  // by the whole finance ladder for ordinary CRUD, so leaving either inside the
  // wildcard would grant a privacy power nobody chose to grant (ADR-0003).
  // Every holder is therefore an EXPLICIT grant in PRESET_ROLES.
  'reveal_pii',
  'reset_password',
]);

/** Ergonomic builder: `perm('member', 'read')` → `'member:read'`. */
export function perm<R extends Resource, A extends Action>(resource: R, action: A): `${R}:${A}` {
  return `${resource}:${action}` as const;
}

/** `true` if `granted` (e.g. role-loaded set) allows `required`. Honors wildcards. */
export function allows(granted: ReadonlySet<string> | readonly string[], required: Permission): boolean {
  const set = granted instanceof Set ? granted : new Set(granted);
  if (set.has('*:*')) return true;
  if (set.has(required)) return true;
  const [resource, action] = required.split(':') as [Resource, Action];
  // `impersonate`, `reveal_pii` and `reset_password` are carved out of the
  // `<resource>:*` wildcard (see expand): each needs `*:*` or an explicit
  // `<resource>:<action>` grant, both handled above. A bare `<resource>:*` must
  // NOT resolve them.
  if (WILDCARD_EXCLUDED_ACTIONS.has(action)) return false;
  return set.has(`${resource}:*`);
}

/** Expand a list that may contain wildcards into a concrete permission list (useful for seeds). */
export function expand(permissions: readonly Permission[]): Permission[] {
  const out = new Set<Permission>();
  for (const p of permissions) {
    if (p === '*:*') {
      for (const r of RESOURCES) for (const a of ACTIONS) out.add(`${r}:${a}`);
      continue;
    }
    const [resource, action] = p.split(':') as [Resource, string];
    if (action === '*') {
      for (const a of ACTIONS) {
        if (WILDCARD_EXCLUDED_ACTIONS.has(a)) continue;
        out.add(`${resource}:${a}`);
      }
      continue;
    }
    out.add(p);
  }
  return [...out];
}
